import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ProductRequirementGroup, RequirementGroupPlacement } from '../product-requirements-config.dto';

interface Props {
    open: boolean;
    mode: 'create' | 'edit';
    group?: ProductRequirementGroup | null;
    onClose: () => void;
    /** code is empty on edit (immutable, not editable). */
    onSubmit: (values: { code: string; name: string; placement: RequirementGroupPlacement }) => void;
    saving?: boolean;
}

export default function GroupEditorDialog({ open, mode, group, onClose, onSubmit, saving }: Props) {
    const { t } = useTranslation();
    const [code, setCode] = useState(group?.code ?? '');
    const [name, setName] = useState(group?.name ?? '');
    const [placement, setPlacement] = useState<RequirementGroupPlacement>(group?.placement ?? 'data_collection');

    const canSave = name.trim() !== '' && (mode === 'edit' || code.trim() !== '');
    // Placement binds the fields' entity type — the backend rejects changing it
    // while the group still has fields, so don't offer the trap (GEN-591).
    const placementLocked = mode === 'edit' && (group?.requirementFields.length ?? 0) > 0;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{mode === 'create' ? t('Add group') : t('Edit group')}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        fullWidth
                        size="small"
                        required
                        label={t('Code')}
                        helperText={t('Stable identifier — conditions reference it. Cannot be changed later.')}
                        value={code}
                        disabled={mode === 'edit'}
                        onChange={(e) => setCode(e.target.value)}
                    />
                    <TextField
                        fullWidth
                        size="small"
                        required
                        label={t('Name')}
                        helperText={t('Shown as the data-collection tab title.')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <TextField
                        fullWidth
                        size="small"
                        select
                        label={t('Placement')}
                        helperText={
                            placementLocked
                                ? t('Placement is fixed while the group has fields.')
                                : t('Top-level tab, or the Services → Information sub-tab (service-level fields).')
                        }
                        value={placement}
                        disabled={placementLocked}
                        onChange={(e) => setPlacement(e.target.value as RequirementGroupPlacement)}
                    >
                        <MenuItem value="data_collection">{t('Data collection (top-level tab)')}</MenuItem>
                        <MenuItem value="service_information">{t('Service information (Services tab)')}</MenuItem>
                    </TextField>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('Cancel')}</Button>
                <Button
                    variant="contained"
                    disabled={!canSave || saving}
                    onClick={() => onSubmit({ code, name, placement })}
                >
                    {t('Save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
